import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { MessageService } from "primeng/api";
import { Observable } from "rxjs";
import { DeleteCautionModalComponent } from "../../../components/modal-templates/delete-caution-modal/delete-caution-modal.component";
import { DeleteConfirmationComponent } from "../../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { EditorItemModalComponent } from "../../../components/modal-templates/generic-modal/editor-item-modal.component";
import { SharedImports } from "../../../components/shared.module";
import { AutomationActions } from "../../../core/ngrx/actions/automation.actions";
import {
  selectAutomationError,
  selectCombinedReusableComponent,
  selectLoading,
} from "../../../core/ngrx/selectors/automation.selectors";
import { reusableComponentDeleteTableColumns, reusableScriptDeleteTableColumns } from "../../../core/table-templates/application-delete-caution-table";
import { reusableComponentTableColumns } from "../../../core/table-templates/reusable-component-table";
import { MessageEnum } from "../../../core/utils/message.enum";
import { BaseLayoutComponent } from "../../base-layout.component";

@Component({
  selector: "app-reusable-components",
  standalone: true,
  imports: [CommonModule, ...SharedImports, DeleteConfirmationComponent],
  templateUrl: "./reusable-components.component.html",
  styleUrl: "./reusable-components.component.scss",
})
export class ReusableComponentsComponent
  extends BaseLayoutComponent
  implements OnInit {
  columns: any = reusableComponentTableColumns;
  scripts: any;
  automationProjectID: any;
  folders: any;
  selectCombinedReusableComponent$!: Observable<any>;
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;
  selectedNode: any;
  selectedComponents: any = [];
  selectedScripts: any = [];
  iconSrcDelete: string =
    "../../../../../assets/images/virtusa-icons/remove-icon-dark.svg";
  private modalService = inject(NgbModal);
  projectID: string = "";
  addReusableComponentFields: any = [];
  addButtonText: string = "Add New Component Folder";
  removeButtonText: string = "Remove Component Folders";
  isFolder: boolean = false;
  selectedFolder: any;
  isLoading = false;
  selectedFramework:string = "";
  user: any;

  private messageService = inject(MessageService);

  async ngOnInit() {
    // this.user = await this.keycloakService.loadUserProfile();
    this.initializeReusableData();
  }

  initializeReusableData() {
    this.projectService.getAutomationProjectId().subscribe((id: any) => {
      this.automationProjectID = id;
    });

    this.selectCombinedReusableComponent$ = this.store.select(
      selectCombinedReusableComponent
    );

    this.projectService.getFrameworkType().subscribe((type: any) => {
      this.selectedFramework = type;
    });

    this.automatiCScriptService.getActiveNode().subscribe((item: any) => {
      this.selectedNode = item;
      this.determineFolderStatus();
      // this.folders = (item.children ?? []).filter(
      //   (item: any) => item?.type === "reusable_component_folder"
      // );
      this.folders = (item.children ?? [])
        .filter((item: any) => item?.type === "reusable_component_folder")
        .map((item: any) => ({
          ...item,
          isSelectable: true,
          isEditable: true,
        }));
    });

    this.projectService.getProjectId().subscribe((id) => {
      if (id != null) {
        this.projectID = id;
      }
    });

    this.loading$ = this.store.select(selectLoading);
    this.error$ = this.store.select(selectAutomationError);
    this.loadNgRxData();
  }

  loadNgRxData() {
    this.selectCombinedReusableComponent$.subscribe((item) => {
      let filteredScripts: any = [];
      if (this.selectedNode?.type === "reusable_component_folder") {
        this.selectedComponents = [];
        this.addButtonText = "Add New Component";
        this.removeButtonText = "Remove Component";

        filteredScripts = (item.reusableScript ?? []).filter(
          (item: any) =>
            item?.automationScriptGroup?.id === this.selectedNode?.id
        );
        this.paramTableData(filteredScripts);
      } else if (this.selectedNode?.type === "REUSABLE_SCRIPT") {
        this.addButtonText = "";
        this.removeButtonText = "";

        filteredScripts = (item.reusableScript ?? []).filter(
          (item: any) => item?.id === this.selectedNode?.id
        );
        this.paramTableData([...filteredScripts]);
      } else if (this.selectedNode?.type === "reusable_component") {
        this.selectedComponents = [];
        this.addButtonText = "Add New Component Folder";
        this.removeButtonText = "Remove Component Folders";
        filteredScripts = item.reusableScript.filter(
          (item: any) => !item.automationScriptGroup
        );
      }

      this.paramTableData(filteredScripts);
    });
  }

  paramTableData(item: any) {
    this.scripts = item;
    this.scripts = (this.scripts ?? []).map((item: any) => {
      const clonedItem = { ...item }; // Create a shallow copy of the item

      clonedItem.paramType = "-";
      clonedItem.paramName = "-";

      const children = (clonedItem.scriptParameters ?? [])
        .filter((param: any) => param.active)
        .map((param: any) => ({
          inputOutput: param.type === "INPUT" ? "Input" : "Output",
          paramName: param.name || "-",
          paramType: param.dataType || "-",
        }));

      return {
        ...clonedItem,
        ...(children.length > 0 ? { children } : {}),
        scriptParameters: undefined,
      };
    });
  }

  async handleTableActions(event: any) {
    switch (event?.type) {
      case "conditional-button":
        // Copy: pushing into the service list would leak the script row into the live breadcrumb.
        const breadcrumbData = [...this.breadcrumbService.list];
        let root = [{
          name: this.dataStore.data.name,
          key: 'projectName'
        }];
        if (!breadcrumbData.find((ele: any) => ele.name === event?.data?.row?.name)) {
          breadcrumbData.push(
            {
              name: event?.data?.row?.name,
              key: event?.data?.row?.id
            }
          );
        }
        sessionStorage.setItem('breadcrumb', JSON.stringify(root.concat(breadcrumbData)));

          this.restService.getEditorStatus(this.dataStore?.data?.id, this.automationProjectID, event?.data?.row?.id).subscribe({
          next: (response) => {
            if (response?.data?.canOpen) {
              this.setEditorOpenedStatus(this.dataStore?.data?.id, this.automationProjectID, event?.data?.row?.id);
              const newUrl = this.buildAppUrl(
                `project/${this.dataStore?.data?.id}/automation/${this.automationProjectID}/script/${event?.data?.row?.id}/editor-view`
              );
              window.open(newUrl, "_blank");
            } else {
              this.toaster.pop({ type: 'warning', text: response?.data?.message });
              this.setEditorOpenedStatus(this.dataStore?.data?.id, this.automationProjectID, event?.data?.row?.id);
            }
          },
          error: (error) => {
            console.error("Error checking editor status:", error);
          }
        });

        break;

      case "conditional-svg":
        this.openGenericModal(true, event?.data?.row, "reusable_component");
        break;
      default:
        break;
    }
  }

  setEditorOpenedStatus(projectId: string, automationProjectId: string, scriptId: string) {
    this.restService.setEditorOpenStatus(projectId, automationProjectId, scriptId).subscribe({
      next: (response) => {
        console.log(response);
      },
      error: (error) => {
        console.error("Error setting editor status:", error);
      }
    });
  }
  
  handleRowSelect(event: any) {
    this.selectedComponents = event.data;
    this.selectedScripts = event?.data;
  }

  addNewComponent() {
    if (this.selectedNode?.type === "reusable_component_folder") {
      this.addReusableComponentFields = [
        {
          name: "Reusable Component Name",
          dataType: "string",
          id: "name",
          placeholder: "Reusable Component Name",
        },
      ];

      this.openGenericModal(false, {}, "reusable_components_folder");
    } else if (this.selectedNode?.type === "reusable_component") {
      this.addReusableComponentFields = [
        {
          id: "name",
          key: "folderName",
          name: "Reusable Components Folder Name",
          dataType: "string",
          placeholder: "Reusable Components Folder Name",
          required: false,
        },
      ];
      this.openGenericModal(false, {}, "reusable_component");
    }
  }

  openGenericModal(isEdit: boolean, row: any, reusableType: any) {
    const modalParams = this.buildModalParams(isEdit);
    const modalRef = this.openModal(modalParams);
    
    this.configureModalFields(modalRef, isEdit, row, reusableType);
    
    modalRef.result.then((result: any) => 
      this.handleModalResult(isEdit, result, row, reusableType)
    );
  }

  private buildModalParams(isEdit: boolean): any {
    const isComponentFolder = this.addButtonText === "Add New Component Folder";
    
    return {
      title: this.getModalTitle(isEdit, isComponentFolder),
      isEdit: isEdit,
    };
  }

  private getModalTitle(isEdit: boolean, isComponentFolder: boolean): string {
    if (isComponentFolder) {
      return isEdit ? "Update Reusable Component Folder" : "Add Reusable Component Folder";
    }
    return isEdit ? "Update Reusable Component" : "Add New Reusable Component";
  }

  private openModal(modalParams: any): any {
    const modalRef = this.modalService.open(EditorItemModalComponent, {
      centered: true,
      windowClass: "add-item-modal",
      backdrop: 'static',
    });
    modalRef.componentInstance.configs = modalParams;
    return modalRef;
  }

  private configureModalFields(modalRef: any, isEdit: boolean, row: any, reusableType: any): void {
    if (isEdit) {
      this.setupEditFields(modalRef, row, reusableType);
    } else {
      modalRef.componentInstance.fields = this.addReusableComponentFields;
    }
  }

  private setupEditFields(modalRef: any, row: any, reusableType: any): void {
    this.addReusableComponentFields = this.getFieldsForType(reusableType);
    
    const fields = this.addReusableComponentFields.map((field: any) => ({
      ...field,
      value: row[field.id] || "",
    }));
    
    modalRef.componentInstance.fields = fields;
  }

  private getFieldsForType(reusableType: any): any[] {
    if (reusableType === "reusable_component_folder") {
      return [
        {
          id: "name",
          key: "name",
          name: "Reusable Components Folder Name",
          dataType: "string",
          placeholder: "Reusable Components Folder Name",
          required: false,
        },
      ];
    }
    
    return [
      {
        name: "Reusable Component Name",
        dataType: "string",
        id: "name",
        placeholder: "Reusable Component Name",
      },
    ];
  }

  private handleModalResult(isEdit: boolean, result: any, row: any, reusableType: any): void {
    if (isEdit) {
      this.handleEditSubmit(result, row, reusableType);
    } else {
      this.handleAddSubmit(result, reusableType);
    }
  }

  private handleEditSubmit(result: any, row: any, reusableType: any): void {
    if (reusableType === "reusable_component_folder") {
      this.updateComponentFolder(result, row);
    } else {
      this.updateReusableScript(result, row);
    }
  }

  private updateComponentFolder(result: any, row: any): void {
    if (this.isNameExists(result?.value?.name, this.folders, row?.id)) {
      this.toaster.pop({ type: 'error', text: MessageEnum.REUSABLE_COMPONENT_NAME_DUPLICATE });
      return;
    }
    
    this.store.dispatch(
      AutomationActions.updateReusableComponent({
        projectId: this.projectID,
        automationProjectId: this.automationProjectID,
        id: row?.id,
        changes: {
          name: result?.value?.name,
          type: "REUSABLE_SCRIPT",
        },
      })
    );
  }

  private updateReusableScript(result: any, row: any): void {
    if (this.isNameExists(result?.value?.name, this.scripts, row?.id)) {
      this.toaster.pop({ type: 'error', text: MessageEnum.REUSABLE_SCRIPT_NAME_DUPLICATE });
      return;
    }
    
    this.store.dispatch(
      AutomationActions.updateReusableScript({
        projectId: this.projectID,
        automationProjectId: this.automationProjectID,
        id: row?.id,
        changes: {
          name: result?.value?.name,
          type: "REUSABLE_SCRIPT",
          automationGroupId: row?.id,
        },
      })
    );
  }

  private handleAddSubmit(result: any, reusableType: any): void {
    if (reusableType === "reusable_components_folder") {
      this.addNewScript(result);
    } else {
      this.addNewComponentFolder(result);
    }
  }

  private addNewScript(result: any): void {
    if (this.isNameExists(result?.value?.name, this.scripts)) {
      this.toaster.pop({ type: 'error', text: MessageEnum.REUSABLE_SCRIPT_NAME_DUPLICATE });
      return;
    }
    
    if (!this.selectedFramework) {
      this.toaster.pop({ type: 'error', text: MessageEnum.FRAMEWORK_TYPE_SELECTION_REQUIRED });
      return;
    }
    
    this.store.dispatch(
      AutomationActions.addReusableScript({
        projectId: this.projectID,
        automationProjectId: this.automationProjectID,
        reusableScript: {
          name: result?.value?.name,
          type: "REUSABLE_SCRIPT",
          automationGroupId: this.selectedNode?.id,
        },
      })
    );
  }

  private addNewComponentFolder(result: any): void {
    if (this.isNameExists(result?.value?.name, this.folders)) {
      this.toaster.pop({ type: 'error', text: MessageEnum.REUSABLE_COMPONENT_NAME_DUPLICATE });
      return;
    }
    
    this.store.dispatch(
      AutomationActions.addReusableComponent({
        projectId: this.projectID,
        automationProjectId: this.automationProjectID,
        reusableComponent: {
          name: result?.value?.name,
          type: "REUSABLE_SCRIPT",
        },
      })
    );
  }

  removeComponents() {
    let idList: any = [];
    // if (this.selectedComponents.length) {
    //   if(this.selectedComponents[0].type === 'reusable_component_folder') {
    //     this.selectedComponents.forEach((component: any) => {
    //       idList = idList.concat(component?.children.map((item: any) => item.id));
    //     });
    //   } else {
    //     idList = this.selectedComponents.map((item: any) => item.id);
    //   }
    // }

    if (this.selectedScripts.length) {
      idList = this.selectedComponents.map((item: any) => item.id);
    } else {
      this.selectedComponents.forEach((component: any) => {
        idList = idList.concat(component?.children.map((item: any) => item.id));
      });
    }


    this.restService.deleteValidationReusableScript(this.projectID, this.automationProjectID, idList).subscribe((res: any) => {
      this.isLoading = false;
      let rowData = res?.data;
      let deletableIds = [];
      if(this.selectedScripts.length) {
        deletableIds = this.selectedScripts
        .filter((item: any) => !rowData?.some((d: any) => d.id === item.id))
        .map((item: any) => item.id);
      } else {
        deletableIds = this.selectedComponents
        .filter((item: any) => !rowData?.some((d: any) => d.id === item.id))
        .map((item: any) => item.id);
      }

      console.log("deletable ids ->", deletableIds);
      const data = {
        deleteList: deletableIds,
        skippedList: rowData,
      };

      if (
        Array.isArray(data?.skippedList) &&
        data?.skippedList?.length > 0
      ) {
        if(this.selectedScripts?.length) {
          this.removeScriptsCaution(data);
        } else {
          this.removeComponentsCaution(data);
        }
      } else {
        this.deletePrompt();
      }
    });
  }

  removeComponentsCaution(tableData: any) {
    let deletableComponents: any = [];
    this.selectedComponents.forEach((component: any) => {
      component.children.forEach((script: any) => {
        if(tableData.skippedList.find((skippedScript: any) => skippedScript.id === script.id)) {
          component.skip = true;
        }
      });
    });
    deletableComponents = this.selectedComponents.filter((ele: any) => !ele.skip).map((component: any) => component.id);

    tableData.skippedList.forEach((ele: any) => {
      this.selectedComponents.forEach((component: any) => {
        let found = component.children.find((child: any) => child.id === ele.id);
        if(found) {
          ele.componentName = component.name;
        }
      });
    });
    this.isLoading = false;
    var nonDeletableAppCount = tableData.skippedList.length;
    let modalRef = this.modalService.open(DeleteCautionModalComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = {
      subTitle: "Unable to Delete Folder",
      cautionMessage1:
        `${nonDeletableAppCount} of the component of this folder ${nonDeletableAppCount > 1 ? 'are' : 'is'} currently used in test scripts. To avoid breaking existing tests, these folders cannot be deleted.`,
      cautionMessage2: "Only folders that are not currently in use will be removed.",
      footerMessage1:
        deletableComponents.length ? "You can delete the remaining folders that are not used in any scripts." : '',
      footerMessage2:
        "To delete the folder, first remove the component's references from the test scripts mentioned above.",
      tableData: tableData.skippedList,
      tableTitle: "Details of Components in Use",
      deleteButtonText: deletableComponents.length ? "Delete Unused Folders Only" : '',
      columns: reusableComponentDeleteTableColumns,
    };

    modalRef.result.then((result) => {
      if (result) {
        //delete scripts / bulk
        if (deletableComponents.length > 0) {
          this.deleteAPI(deletableComponents);
        }
      }
    });

  }

  deleteAPI(ids?: any[]) {
    if (this.selectedScripts.length > 0) {
      // //delete reusable script / bulk
      this.store.dispatch(
        AutomationActions.deleteReusableScripts({
          projectId: this.projectID,
          automationProjectId: this.automationProjectID,
          idList: ids?.length ? ids : this.selectedScripts.map((item: any) => item.id),
        })
      );
    } else {
      // //delete reusable component / bulk
      this.store.dispatch(
        AutomationActions.deleteReusableComponents({
          projectId: this.projectID,
          automationProjectId: this.automationProjectID,
          idList: ids?.length ? ids : this.selectedComponents.map((item: any) => item.id),
        })
      );
    }
  }

  deletePrompt() {
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });
    modalRef.result.then((result: any) => {
      if (result) {
        this.deleteAPI();
      }
    });
  }


  removeScriptsCaution(tableData: any) {
    this.isLoading = false;
    var nonDeletableAppCount = tableData.skippedList.length;
    let modalRef = this.modalService.open(DeleteCautionModalComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = {
      subTitle: "Unable to Delete All Selected Scripts",
      cautionMessage1:
        `${nonDeletableAppCount} of the selected scripts ${nonDeletableAppCount > 1 ? 'are' : 'is'} currently used in test scripts. To avoid breaking existing tests, these scripts cannot be deleted.`,
      cautionMessage2: "Only scripts that are not currently in use will be removed.",
      footerMessage1:
        tableData.deleteList?.length ? "You can delete the remaining scripts that are not used in any scripts." : '',
      footerMessage2:
        "To delete the listed scripts, first remove their references from the test scripts mentioned above.",
      tableData: tableData.skippedList,
      tableTitle: "Details of Scripts in Use",
      deleteButtonText: tableData.deleteList ? "Delete Unused Scripts Only" : '',
      columns: reusableScriptDeleteTableColumns,
    };
    modalRef.result.then((result) => {
      if (result) {
        //delete scripts / bulk
        if (tableData.deleteList.length > 0) {
          this.deleteAPI(tableData.deleteList);
        }
      }
    });
  }

  changeRemoveIcon(isActive: boolean) {
    this.iconSrcDelete = isActive
      ? "../../../../../assets/images/virtusa-icons/remove-icon-light.svg"
      : "../../../../../assets/images/virtusa-icons/remove-icon-dark.svg";
  }

  handleSelectedCards(selectedCards: any[]) {
    this.selectedComponents = selectedCards;
    console.log("Selected Cards:", this.selectedComponents);
  }

  onEditFolder(event: any) {
    this.openGenericModal(true, event?.$event, "reusable_component_folder");
  }

  refreshData() {
    this.store.dispatch(
      AutomationActions.loadReusableComponent({
        projectId: this.projectID,
        automationProjectId: this.automationProjectID,
      })
    );
  }

  isNameExists(name: string, dataList: any, excludeId?: string): boolean {
    const normalizedName = name?.toLowerCase()?.trim();
    return dataList
      ?.filter((page: any) => page?.id !== excludeId) // exclude current editing item
      ?.some((page: any) => page?.name?.toLowerCase()?.trim() === normalizedName);
  }

  private determineFolderStatus(): void {
    if (this.selectedNode?.type === "reusable_component_folder") {
      this.handleFolderNode();
    } else if (this.selectedNode?.type === "REUSABLE_SCRIPT") {
      this.handleReusableScriptNode();
    } else {
      this.isFolder = false;
    }
  }

  private handleFolderNode(): void {
    this.isFolder = true;
    this.selectedFolder = this.selectedNode;
  }

  private handleReusableScriptNode(): void {
    const parentType = this.selectedNode?.parent?.type;

    if (parentType === "reusable_component_folder") {
      this.setFolderFromParent();
    } else if (parentType === "REUSABLE_SCRIPT") {
      this.findAndSetFolderFromAncestors();
    } else {
      this.isFolder = false;
    }
  }

  private setFolderFromParent(): void {
    this.isFolder = true;
    this.selectedFolder = this.selectedNode.parent;
  }

  private findAndSetFolderFromAncestors(): void {
    const folderNode = this.findLevel2Folder(this.selectedNode.parent);

    if (this.isValidFolder(folderNode)) {
      this.isFolder = true;
      this.selectedFolder = folderNode;
    } else {
      this.isFolder = false;
    }
  }

  private findLevel2Folder(startNode: any): any {
    let currentNode = startNode;
    while (currentNode && currentNode.level !== 2) {
      currentNode = currentNode.parent;
    }
    return currentNode;
  }

  private isValidFolder(node: any): boolean {
    return node?.level === 2 && node?.type === "reusable_component_folder";
  }
}
